/**
 * TranscriptPanel Component
 *
 * A slide-in sheet that shows the full podcast transcript, synced to
 * the current playback position. Clicking a line seeks the player there.
 *
 * Supports a "language learning" mode where each spoken line is shown
 * with a translation directly below it — ideal for following along in
 * the podcast language while reading your native language.
 *
 * - Active line is highlighted and scrolled into view automatically.
 * - Language learning toggle is persisted in localStorage.
 * - Multiple translations can be generated per podcast (stored as a JSON map).
 * - Polling detects when a newly generated translation is ready.
 */

import { useEffect, useRef, useMemo, useCallback, memo, useState } from "react";
import {
	FileText,
	Loader2,
	AlertCircle,
	Globe,
	CheckCircle2,
	X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { usePlayerStore } from "@/lib/player/player.store";
import {
	useTranscript,
	findActiveLine,
	expandSegmentToLines,
	type TranscriptLine,
} from "./hooks/useTranscript.hook";
import { TranscriptLineRow } from "./transcriptLineRow.component";
import { PodcastService } from "@/lib/podcast/podcast.service";
import {
	useTranscriptStore,
	getTranscriptActions,
} from "@/lib/transcript/transcript.store";
import { useTranslation } from "react-i18next";
import type { TranscriptChapter } from "@/lib/podcast/podcast.service";

// ─── Language registry ────────────────────────────────────────────────────────

const TRANSLATABLE_LANGUAGES: { code: string; label: string }[] = [
	{ code: "en", label: "English" },
	{ code: "de", label: "Deutsch" },
	{ code: "fr", label: "Français" },
	{ code: "es", label: "Español" },
	{ code: "it", label: "Italiano" },
	{ code: "pt", label: "Português" },
	{ code: "nl", label: "Nederlands" },
	{ code: "pl", label: "Polski" },
	{ code: "ru", label: "Русский" },
	{ code: "ja", label: "日本語" },
	{ code: "zh", label: "中文" },
	{ code: "ko", label: "한국어" },
	{ code: "tr", label: "Türkçe" },
	{ code: "sv", label: "Svenska" },
];

function getLanguageLabel(code: string): string {
	return (
		TRANSLATABLE_LANGUAGES.find((l) => l.code === code)?.label ??
		code.toUpperCase()
	);
}

// ─── Transcript skeleton ──────────────────────────────────────────────────────

// Line widths cycle to mimic varied natural speech lengths
const LINE_WIDTHS = ["w-full", "w-4/5", "w-3/4", "w-full", "w-2/3", "w-5/6"];

function TranscriptSkeleton({ isDualLanguage }: { isDualLanguage: boolean }) {
	const chapters = [{ lines: 5 }, { lines: 4 }];

	return (
		<div className="pt-3">
			{chapters.map((ch, ci) => (
				<div key={ci} className="mb-5">
					<div className="flex items-center gap-2 mb-2 py-1.5">
						<Skeleton className="h-5 w-5 rounded-full shrink-0" />
						<Skeleton className="h-3 w-32" />
					</div>

					{Array.from({ length: ch.lines }).map((_, li) => {
						const widthClass =
							LINE_WIDTHS[(ci * 10 + li) % LINE_WIDTHS.length] ?? "w-full";
						return (
							<div key={li} className="rounded-lg px-2.5 py-2 mb-0.5">
								{isDualLanguage ? (
									<div className="space-y-1.5">
										<Skeleton className={`h-3.5 ${widthClass}`} />
										<Skeleton
											className={`h-3 opacity-60 ${
												LINE_WIDTHS[(ci * 10 + li + 2) % LINE_WIDTHS.length]
											}`}
										/>
									</div>
								) : (
									<Skeleton className={`h-3.5 ${widthClass}`} />
								)}
							</div>
						);
					})}
				</div>
			))}
		</div>
	);
}

// ─── Chapter Section ──────────────────────────────────────────────────────────

interface ChapterSectionProps {
	chapter: TranscriptChapter;
	chapterIndex: number;
	activeSegmentIndex: number | null;
	activeLineIndex: number | null;
	activeLineRef: React.RefObject<HTMLDivElement | null>;
	isDualLanguage: boolean;
	isTranslating: boolean;
	translationLangCode: string;
}

const ChapterSection = memo(function ChapterSection({
	chapter,
	chapterIndex,
	activeSegmentIndex,
	activeLineIndex,
	activeLineRef,
	isDualLanguage,
	isTranslating,
	translationLangCode,
}: ChapterSectionProps) {
	const allLines = useMemo(() => {
		const result: Array<{
			line: TranscriptLine;
			segmentIndex: number;
			lineIndex: number;
		}> = [];
		chapter.segments.forEach((seg, si) => {
			expandSegmentToLines(
				seg,
				isDualLanguage ? translationLangCode : undefined,
			).forEach((line, li) => {
				result.push({ line, segmentIndex: si, lineIndex: li });
			});
		});
		return result;
	}, [chapter.segments, isDualLanguage, translationLangCode]);

	if (allLines.length === 0) return null;

	return (
		<div className="mb-5">
			<div className="flex items-center gap-2 mb-2 sticky top-0 bg-background/95 backdrop-blur-sm py-1.5 z-10">
				<div className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
					<span className="text-[10px] font-bold text-primary">
						{chapter.index + 1}
					</span>
				</div>
				<p className="text-xs font-semibold text-foreground truncate">
					{chapter.title}
				</p>
			</div>

			<div className="space-y-0.5">
				{allLines.map(({ line, segmentIndex, lineIndex }) => {
					const isActive =
						activeSegmentIndex === segmentIndex &&
						activeLineIndex === lineIndex;
					return (
						<TranscriptLineRow
							key={`${chapterIndex}-${segmentIndex}-${lineIndex}`}
							line={line}
							isActive={isActive}
							isDualLanguage={isDualLanguage}
							isTranslating={isTranslating}
							lineRef={isActive ? activeLineRef : undefined}
						/>
					);
				})}
			</div>
		</div>
	);
});

// ─── Panel ────────────────────────────────────────────────────────────────────

interface TranscriptPanelProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function TranscriptPanel({ open, onOpenChange }: TranscriptPanelProps) {
	const { t, i18n } = useTranslation();
	const uiLanguage = i18n.language.split("-")[0] ?? "en";
	const podcastId = usePlayerStore((s) => s.podcastId);
	const currentTime = usePlayerStore((s) => s.currentTime);
	const podcastLanguage = usePlayerStore(
		(s) => s.podcastChapters[0]?.language ?? "de",
	);
	const podcastLang = podcastLanguage.split("-")[0] ?? podcastLanguage;

	// ── Translation state (from store — survives panel open/close) ──────────

	const isDualLanguage = useTranscriptStore((s) => s.isDualLanguage);
	const translationLangCode = useTranscriptStore((s) => s.translationLangCode);
	const isTranslatingLang = useTranscriptStore((s) =>
		podcastId ? (s.translatingByPodcast[podcastId] ?? null) : null,
	);

	/** Whether the translation generator controls are open (local UI only). */
	const [showTranslateControls, setShowTranslateControls] =
		useState<boolean>(false);

	/** Language selected in the "Generate" dropdown. */
	const [generateLang, setGenerateLang] = useState<string>(
		uiLanguage !== podcastLang ? uiLanguage : "en",
	);

	// ── Fetch transcript (with optional polling) ─────────────────────────────

	const { data: transcript, isLoading, isError } = useTranscript(podcastId);

	// ── Detect translation completion ────────────────────────────────────────

	useEffect(() => {
		if (!isTranslatingLang || !transcript || !podcastId) return;
		const segments = transcript.chapters.flatMap((ch) => ch.segments);
		if (segments.length === 0) return;
		const allDone = segments.every(
			(seg) => !!seg.translations?.[isTranslatingLang],
		);
		if (allDone) {
			getTranscriptActions().completeTranslation(podcastId, isTranslatingLang);
			setShowTranslateControls(false);
		}
	}, [transcript, isTranslatingLang, podcastId]);

	// ── Derive available translations ────────────────────────────────────────

	/** Languages that have at least one translated segment. */
	const availableTranslations = useMemo<string[]>(() => {
		if (!transcript) return [];
		const segments = transcript.chapters.flatMap((ch) => ch.segments);
		if (segments.length === 0) return [];
		const allLangs = [
			...new Set(
				segments.flatMap((seg) => Object.keys(seg.translations ?? {})),
			),
		];
		return allLangs.filter((lang) => lang !== podcastLang);
	}, [transcript, podcastLang]);

	/** Languages that can be targeted for a new translation (not source lang). */
	const targetableLanguages = useMemo(
		() => TRANSLATABLE_LANGUAGES.filter((l) => l.code !== podcastLang),
		[podcastLang],
	);

	// ── Active line tracking ─────────────────────────────────────────────────

	const active = useMemo(
		() => findActiveLine(transcript, currentTime),
		[transcript, currentTime],
	);

	const activeLineRef = useRef<HTMLDivElement | null>(null);
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const prevOpenRef = useRef(false);

	const scrollToActive = useCallback((behavior: ScrollBehavior = "smooth") => {
		const container = scrollContainerRef.current;
		const target = activeLineRef.current;
		if (!container || !target) return;

		const targetTop =
			target.getBoundingClientRect().top -
			container.getBoundingClientRect().top +
			container.scrollTop;

		const STICKY_HEADER_HEIGHT = 44;
		container.scrollTo({
			top: Math.max(0, targetTop - STICKY_HEADER_HEIGHT),
			behavior,
		});
	}, []);

	useEffect(() => {
		if (!open) {
			prevOpenRef.current = false;
			return;
		}
		const justOpened = !prevOpenRef.current;
		prevOpenRef.current = true;
		if (justOpened) {
			const timer = setTimeout(() => scrollToActive("smooth"), 450);
			return () => clearTimeout(timer);
		}
		scrollToActive("smooth");
	}, [
		open,
		active?.chapterIndex,
		active?.segmentIndex,
		active?.lineIndex,
		scrollToActive,
	]);

	// ── Handlers ─────────────────────────────────────────────────────────────

	const handleSelectTranslation = useCallback(
		(lang: string) => {
			const { setIsDualLanguage, setTranslationLangCode } =
				getTranscriptActions();
			if (isDualLanguage && translationLangCode === lang) {
				setIsDualLanguage(false);
			} else {
				setTranslationLangCode(lang);
				setIsDualLanguage(true);
			}
		},
		[isDualLanguage, translationLangCode],
	);

	const handleGenerate = useCallback(async () => {
		if (!podcastId || !transcript || isTranslatingLang) return;
		// Guard: language already fully translated — just switch to it
		const segments = transcript.chapters.flatMap((ch) => ch.segments);
		if (
			segments.length > 0 &&
			segments.every((s) => !!s.translations?.[generateLang])
		) {
			getTranscriptActions().setTranslationLangCode(generateLang);
			getTranscriptActions().setIsDualLanguage(true);
			setShowTranslateControls(false);
			return;
		}
		const chapterIds = transcript.chapters
			.filter((ch) => ch.segments.length > 0)
			.map((ch) => ch.id);
		if (chapterIds.length === 0) return;

		getTranscriptActions().startTranslating(podcastId, generateLang);
		try {
			await PodcastService.triggerPodcastTranslation(
				podcastId,
				chapterIds,
				generateLang,
				podcastLang,
			);
		} catch {
			getTranscriptActions().stopTranslating(podcastId);
		}
	}, [podcastId, transcript, isTranslatingLang, generateLang, podcastLang]);

	// ── Derived display ───────────────────────────────────────────────────────

	const hasTranscript =
		transcript && transcript.chapters.some((ch) => ch.segments.length > 0);
	const originalLang = podcastLang.slice(0, 2).toUpperCase();

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent
				side="right"
				className="w-full sm:w-[420px] flex flex-col p-0 gap-0 z-[80]"
			>
				<SheetHeader className="px-5 pt-5 pb-3 border-b shrink-0">
					{/* ── Title row ─────────────────────────────────────── */}
					<div className="flex items-center justify-between gap-2">
						<SheetTitle className="flex items-center gap-2 text-base shrink-0">
							<FileText className="h-4 w-4 text-primary" />
							{t("transcript.title")}
						</SheetTitle>

						<div className="flex items-center gap-1.5 flex-wrap justify-end">
							{/* Available translation chips */}
							{availableTranslations.map((lang) => {
								const isActive = isDualLanguage && translationLangCode === lang;
								return (
									<button
										key={lang}
										type="button"
										onClick={() => handleSelectTranslation(lang)}
										title={
											isActive
												? t("transcript.translation.hideTranslation")
												: t("transcript.translation.showTranslation", {
														lang: getLanguageLabel(lang),
													})
										}
										className={[
											"flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
											"border transition-colors duration-150",
											isActive
												? "border-primary/40 bg-primary/10 text-primary"
												: "border-border bg-transparent text-muted-foreground hover:text-foreground hover:border-foreground/30",
										].join(" ")}
									>
										{isActive && <CheckCircle2 className="h-3 w-3 shrink-0" />}
										<span>
											{originalLang}
											<span className="mx-0.5 opacity-50">→</span>
											{lang.slice(0, 2).toUpperCase()}
										</span>
									</button>
								);
							})}

							{/* Translate button: open/close translation generator */}
							{hasTranscript && (
								<button
									type="button"
									onClick={() => setShowTranslateControls((v) => !v)}
									className={[
										"flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
										"border transition-colors duration-150",
										showTranslateControls
											? "border-primary/40 bg-primary/10 text-primary"
											: "border-border bg-transparent text-muted-foreground hover:text-foreground hover:border-foreground/30",
									].join(" ")}
								>
									{showTranslateControls ? (
										<X className="h-3 w-3 shrink-0" />
									) : (
										<Globe className="h-3 w-3 shrink-0" />
									)}
									<span>
										{showTranslateControls
											? t("transcript.translation.close")
											: t("transcript.translation.addLanguage")}
									</span>
									{isTranslatingLang && (
										<Loader2 className="h-3 w-3 animate-spin shrink-0" />
									)}
								</button>
							)}
						</div>
					</div>

					{/* ── Translation generator controls ────────────────── */}
					{showTranslateControls && hasTranscript && (
						<div className="mt-2 p-3 rounded-lg border bg-muted/30 space-y-2">
							<p className="text-xs font-medium text-foreground">
								{t("transcript.translation.addLanguage")}
							</p>
							<div className="flex items-center gap-2">
								<Select
									value={generateLang}
									onValueChange={setGenerateLang}
									disabled={!!isTranslatingLang}
								>
									<SelectTrigger className="h-8 text-xs flex-1">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{targetableLanguages.map((l) => (
											<SelectItem
												key={l.code}
												value={l.code}
												className="text-xs"
											>
												{l.label}
												{availableTranslations.includes(l.code) && (
													<span className="ml-1.5 text-[10px] text-primary/70 font-medium">
														✓
													</span>
												)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<Button
									size="sm"
									className="h-8 text-xs px-3 shrink-0"
									onClick={handleGenerate}
									disabled={!!isTranslatingLang}
								>
									{isTranslatingLang === generateLang ? (
										<>
											<Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
											{t("transcript.translation.generating")}
										</>
									) : (
										t("transcript.translation.generate")
									)}
								</Button>
							</div>
							{isTranslatingLang && (
								<p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
									<Loader2 className="h-3 w-3 animate-spin shrink-0" />
									{t("transcript.translation.translatingTo", {
										lang: getLanguageLabel(isTranslatingLang),
									})}
								</p>
							)}
						</div>
					)}

					{/* ── Sub-line ─────────────────────────────────────── */}
					{transcript && (
						<p className="text-xs text-muted-foreground mt-0.5">
							{isDualLanguage
								? t("transcript.dualLanguage.toggleOff")
								: t("transcript.clickToSeek")}
						</p>
					)}
				</SheetHeader>

				{/* ── Body ──────────────────────────────────────────────── */}
				<div
					ref={scrollContainerRef}
					className="flex-1 overflow-y-auto px-4 pb-3"
				>
					{isLoading && <TranscriptSkeleton isDualLanguage={isDualLanguage} />}

					{isError && (
						<div className="flex flex-col items-center justify-center h-40 gap-3 text-muted-foreground">
							<AlertCircle className="h-6 w-6 text-destructive" />
							<p className="text-sm text-center">{t("transcript.error")}</p>
						</div>
					)}

					{!isLoading && !isError && !hasTranscript && (
						<div className="flex flex-col items-center justify-center h-40 gap-3 text-muted-foreground">
							<FileText className="h-6 w-6 opacity-50" />
							<p className="text-sm text-center">
								{t("transcript.notAvailable")}
							</p>
						</div>
					)}

					{hasTranscript &&
						transcript.chapters.map((chapter, ci) => {
							if (!chapter.hasTranscript || chapter.segments.length === 0)
								return null;
							return (
								<ChapterSection
									key={chapter.id}
									chapter={chapter}
									chapterIndex={ci}
									activeSegmentIndex={
										active?.chapterIndex === ci ? active.segmentIndex : null
									}
									activeLineIndex={
										active?.chapterIndex === ci ? active.lineIndex : null
									}
									activeLineRef={activeLineRef}
									isDualLanguage={isDualLanguage}
									isTranslating={!!isTranslatingLang}
									translationLangCode={translationLangCode}
								/>
							);
						})}
				</div>

				{hasTranscript && (
					<div className="px-5 py-3 border-t shrink-0">
						<Button
							variant="outline"
							size="sm"
							className="w-full text-xs"
							onClick={() => scrollToActive("smooth")}
						>
							{t("transcript.scrollToCurrent")}
						</Button>
					</div>
				)}
			</SheetContent>
		</Sheet>
	);
}
